import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Modal,
  Pagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import TuneIcon from '@mui/icons-material/Tune'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { useTranslation } from 'react-i18next'
import { navigate } from '@reach/router'
import { ROUTE_CREATE, ROUTE_FORM_GENERATOR_FORM } from '../../routes/routes-constants'
import { getFormContainer } from '../../container/form-module'
import { FORM_SERVICE_KEY, USER_FORM_SERVICE_KEY } from '../../modules/forms'
import { FormService } from '../../modules/forms/services/FormService'
import { Form } from '../../modules/forms/models/Form'
import { Query, QueryParam } from '../../common/api/Query'
import { AppTable, Field } from '../../components/table'
import { UserFormService } from '../../modules/forms/services/UserFormService'
import { UserForm } from '../../modules/forms/models/UserForm'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import { TrashIcon } from '../../components/icons/TrashIcon'
import { forkJoin, Observable } from 'rxjs'
import { reduceString } from '../../common/utils/strings'
import { IStatusService } from '../../common/status/StatusService'
import { getAppContainer, STATUS_SERVICE_KEY } from '../../container/app'
import { getCategoryLabel } from '../../common/enums/FormCategories'
import { PREDEFINED_FORM_IDS } from '../../common/enums/PredefinedForms'
import { PageLayout } from 'pages/layout'
import { DownloadModal } from './DownloadModal'
import { CustomModal } from '../../components/modal/CustomModal'
import { AssignTo } from './AssignTo'
import { getUserContainer } from '../../container/user-module'
import { IUserService, USER_SERVICE_KEY } from '../../modules/users'
import { User, UserQuery } from '../../modules/users/models/User'

const formContainer = getFormContainer()
const formService = formContainer.get<FormService>(FORM_SERVICE_KEY)
const userFormService = formContainer.get<UserFormService>(USER_FORM_SERVICE_KEY)
const userService = getUserContainer().get<IUserService>(USER_SERVICE_KEY)

const ROWS_PER_PAGE = 5

// Se carga la lista entera y se filtra y pagina en cliente: el backend ordena
// por titulo pero no admite filtro de texto, y el buscador del diseno tiene que
// mirar todos los cuestionarios, no solo la pagina que se este viendo.
const PAGE_SIZE_ALL = 500

export function Table() {
  const { t } = useTranslation()
  const statusService = getAppContainer().get<IStatusService>(STATUS_SERVICE_KEY)

  const [forms, setForms] = useState<Form[]>([])
  const [userForms, setUserForms] = useState<string[]>([])
  // Nombres de los autores. El backend devuelve el id en createdBy; los
  // nombres se resuelven aparte, en una sola peticion, igual que en la tabla
  // de resultados.
  const [authors, setAuthors] = useState<Map<string, string>>(new Map())
  const [search, setSearch] = useState<string>('')
  const [category, setCategory] = useState<string>('')
  const [page, setPage] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null)
  const [downloadModalOpen, setDownloadModalOpen] = useState<boolean>(false)
  const [selectedFormForDownload, setSelectedFormForDownload] = useState<Form | null>(null)
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false)
  const [selectedFormForDelete, setSelectedFormForDelete] = useState<Form | null>(null)
  const [formToAssign, setFormToAssign] = useState<Form | null>(null)

  const getUserForms = (ids: string[]): Observable<UserForm[]> =>
    forkJoin(
      ids.map((id) =>
        userFormService.getFilteredList(
          new Query({ query: [new QueryParam<UserForm>('formID', id)] })
        )
      )
    ) as unknown as Observable<UserForm[]>

  useEffect(() => {
    setIsLoading(true)
    formService
      .getFilteredList(
        new Query({ pager: { offset: 0, limit: PAGE_SIZE_ALL }, sort: [{ field: 'title' }] })
      )
      .subscribe((res) => {
        setForms(res.items)
        setIsLoading(false)

        getUserForms(res.items.map((f) => f.id || '')).subscribe((r) =>
          setUserForms(r.map((uf) => uf.id || ''))
        )

        const authorIDs = Array.from(
          new Set(res.items.map((f) => f.createdBy).filter((id): id is string => !!id))
        )
        if (!authorIDs.length) return

        userService
          .getFilteredList(new Query<UserQuery>({ query: [{ name: 'ids', value: authorIDs }] }))
          .subscribe((users) =>
            setAuthors(
              new Map(users.items.map((u: User) => [u.id, `${u.firstName} ${u.lastName}`.trim()]))
            )
          )
      })
  }, [])

  const categories = useMemo(
    () => Array.from(new Set(forms.map((f) => f.category).filter(Boolean))),
    [forms]
  )

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return forms.filter(
      (f) => (!q || t(f.title).toLowerCase().includes(q)) && (!category || f.category === category)
    )
  }, [forms, search, category, t])

  const paged = useMemo(
    () => filtered.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE),
    [filtered, page]
  )

  const createForm = () => navigate(`${ROUTE_FORM_GENERATOR_FORM}/${ROUTE_CREATE}`)
  const editForm = (form: Form) => navigate(`${ROUTE_FORM_GENERATOR_FORM}/${form.id}`)
  const resultForm = (form: Form) => navigate(`${ROUTE_FORM_GENERATOR_FORM}/${form.id}/results`)
  // El diseno pide un modal sobre el listado, no una pantalla aparte.
  const associateForm = (form: Form) => setFormToAssign(form)

  const handleDeleteClick = (form: Form) => {
    setSelectedFormForDelete(form)
    setOpenDeleteModal(true)
  }

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false)
    setSelectedFormForDelete(null)
  }

  const handleDeleteForm = () => {
    if (!selectedFormForDelete?.id) return

    formService.delete(selectedFormForDelete.id).subscribe(() => {
      setForms((prev) => prev.filter((f) => f.id !== selectedFormForDelete.id))
      statusService.sendStatus({ variant: 'success' })
      setOpenDeleteModal(false)
      setSelectedFormForDelete(null)
    })
  }

  const handleDownloadClick = (form: Form) => {
    if (!form.id) return
    setSelectedFormForDownload(form)
    setDownloadModalOpen(true)
  }

  const formatDate = (d?: Date) =>
    d && !isNaN(d.getTime()) ? d.toLocaleDateString('es-ES') : undefined

  const dash = (value?: string) => (
    <Typography sx={{ fontSize: '14px', color: value ? undefined : 'var(--gray-border)' }}>
      {value || '—'}
    </Typography>
  )

  const hideForm = (form: Form) => (form.id ? userForms.includes(form.id) : false)
  const isPredefinedForm = (form: Form): boolean =>
    form.id ? PREDEFINED_FORM_IDS.includes(form.id) : false

  // Mismo helper que las tablas de usuarios y protocolos, para que las acciones
  // se vean igual en toda la app: iconos grises, la papelera en rojo. Antes
  // eran svg verdes de otro juego de iconos.
  const icon = (
    Icono: React.ElementType,
    label: string,
    handler: () => void,
    hidden = false,
    rojo = false
  ) =>
    hidden ? null : (
      <Tooltip title={t(label)}>
        <IconButton
          size="small"
          onClick={handler}
          sx={{ p: 0.5, color: rojo ? 'var(--destructive, #e14c54)' : '#9b9d9a' }}
        >
          <Icono sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
    )

  const fields: Field<Form>[] = [
    {
      name: 'title',
      label: t('questionnaire'),
      renderFunc: (f, form) => (
        <Box display="flex" alignItems="center" gap={1}>
          <DescriptionOutlinedIcon sx={{ fontSize: 18, color: 'var(--gray-border)' }} />
          <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{t(form.title)}</Typography>
        </Box>
      ),
    },
    {
      name: 'description',
      label: t('description'),
      renderFunc: (f, form) => reduceString(t(form.description ?? ''), 60),
    },
    {
      name: 'id',
      label: t('createdBy'),
      renderFunc: (f, form) => dash(form.createdBy ? authors.get(form.createdBy) : undefined),
    },
    {
      // updatedAt solo existe si el cuestionario se ha editado alguna vez; si
      // no, la ultima modificacion es la propia alta.
      name: 'repeating',
      label: t('lastModified'),
      renderFunc: (f, form) => dash(formatDate(form.updatedAt ?? form.createdAt)),
    },
    {
      name: 'category',
      label: t('actions'),
      renderFunc: (f, form) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          {icon(VisibilityOutlinedIcon, 'see', () => resultForm(form))}
          {icon(
            EditOutlinedIcon,
            'edit',
            () => editForm(form),
            isPredefinedForm(form) || hideForm(form)
          )}
          {/* Los iconos son los del mockup: ojo, lapiz, portapapeles y avion de
              papel, en ese orden. El avion es asignar el cuestionario. */}
          {icon(AssignmentOutlinedIcon, 'download', () => handleDownloadClick(form), !form.id)}
          {icon(SendOutlinedIcon, 'assign', () => associateForm(form))}
          {icon(
            TrashIcon,
            'delete',
            () => handleDeleteClick(form),
            isPredefinedForm(form) || hideForm(form),
            true
          )}
        </Box>
      ),
    },
  ]

  return (
    <PageLayout>
      <Modal open={openDeleteModal} onClose={handleCloseDeleteModal}>
        <CustomModal
          title={t('deleteForm')}
          warningText={t('irreversibleFormAction')}
          handleClose={handleCloseDeleteModal}
          handleSave={handleDeleteForm}
        />
      </Modal>

      {/* width al 100%: .innerCard de PageLayout centra a los hijos que no lo declaran */}
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        width="100%"
        mb={3}
        gap={2}
      >
        <Box>
          <Typography sx={{ fontSize: '22px', fontWeight: 600, color: 'var(--gray-medium)' }}>
            {t('questionnaires')}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: 'var(--gray-text)' }}>
            {t('questionnairesSubtitle')}
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={createForm}
          sx={{
            textTransform: 'none',
            borderRadius: 'var(--button-radius)',
            boxShadow: 'none',
            whiteSpace: 'nowrap',
            backgroundColor: 'var(--green-primary)',
            '&:hover': { backgroundColor: 'var(--green-dark)', boxShadow: 'none' },
          }}
        >
          {t('createNewQuestionnaire')}
        </Button>
      </Box>

      <Box display="flex" alignItems="center" width="100%" gap={1.5} mb={2}>
        <TextField
          size="small"
          placeholder={t('searchQuestionnaireByName')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(0)
          }}
          sx={{ flexGrow: 1, maxWidth: '420px', '& fieldset': { borderRadius: '6px' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'var(--gray-border)' }} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="outlined"
          startIcon={<TuneIcon />}
          onClick={(e) => setFilterAnchor(e.currentTarget)}
          sx={{
            textTransform: 'none',
            borderRadius: 'var(--button-radius)',
            borderColor: 'var(--gray-border)',
            color: 'var(--gray-text)',
            '&:hover': { borderColor: 'var(--green-primary)', backgroundColor: 'transparent' },
          }}
        >
          {t('filters')}
        </Button>
      </Box>

      <Menu
        anchorEl={filterAnchor}
        open={Boolean(filterAnchor)}
        onClose={() => setFilterAnchor(null)}
      >
        <MenuItem
          selected={category === ''}
          onClick={() => {
            setCategory('')
            setPage(0)
            setFilterAnchor(null)
          }}
        >
          {t('all')}
        </MenuItem>
        {categories.map((c) => (
          <MenuItem
            key={c}
            selected={category === c}
            onClick={() => {
              setCategory(c)
              setPage(0)
              setFilterAnchor(null)
            }}
          >
            {t(getCategoryLabel(c))}
          </MenuItem>
        ))}
      </Menu>

      <AppTable items={paged} rowKeyField="id" fields={fields} isLoading={isLoading} />

      {!isLoading && filtered.length === 0 && (
        <Box py={4} textAlign="center">
          <Typography color="text.secondary">{t('noForms')}</Typography>
        </Box>
      )}

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        width="100%"
        gap={1}
        mt={2}
      >
        {' '}
        <Pagination
          count={Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE))}
          page={page + 1}
          onChange={(_, p) => setPage(p - 1)}
          shape="rounded"
          sx={{
            '& .Mui-selected': {
              backgroundColor: 'var(--green-primary) !important',
              color: 'var(--white)',
            },
          }}
        />
      </Box>

      <DownloadModal
        open={downloadModalOpen}
        form={selectedFormForDownload}
        onClose={() => {
          setDownloadModalOpen(false)
          setSelectedFormForDownload(null)
        }}
      />

      <AssignTo
        open={Boolean(formToAssign)}
        formID={formToAssign?.id || ''}
        onClose={() => setFormToAssign(null)}
      />
    </PageLayout>
  )
}
